/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import type dayjs from 'dayjs'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { resolveDivisionFieldGates } from '@sportninja/common/features/competition/operations/utils/divisionFieldGates'
import { TextInput } from 'src/components/ts-components/TextInput'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import { NumberInput } from 'src/components/ts-components/NumberInput'
import DatePicker from 'src/components/ts-components/DatePicker'

/** Child competition types, in hierarchy order. */
export const COMPETITION_TYPES = ['Season', 'Conference', 'Division'] as const

/**
 * Canlan option lists, copied verbatim from the legacy `canlanDivisionForm`
 * (`web/src/components/Form/form-configs.js`). Values are stored as-is on the
 * schedule, so these strings must match legacy exactly — including the stray
 * space in 'Tuesday /Mardi' and the out-of-order 'Thursday/Jeudi' at the end.
 * "Cleaning them up" would stop existing records matching their own option.
 */
export const DAY_OF_WEEK_OPTIONS = [
  'N/A',
  'Friday',
  'Friday - AM',
  'Friday - PM',
  'Monday',
  'Monday - AM',
  'Monday - PM',
  'Monday - Saturday',
  'Saturday',
  'Saturday - AM',
  'Saturday - PM',
  'Sunday',
  'Sunday - AM',
  'Sunday - PM',
  'Sunday - Thursday',
  'Thursday',
  'Thursday - AM',
  'Thursday - PM',
  'Tuesday',
  'Tuesday - AM',
  'Tuesday - PM',
  'Wednesday',
  'Wednesday - AM',
  'Wednesday - PM',
  'Weekdays',
  'Weekends',
  'Monday/Lundi',
  'Tuesday /Mardi',
  'Wednesday/Mercredi',
  'Friday/Vendredi',
  'Saturday Afternoon/Samedi Jour',
  'Saturday Night/Samedi Soir',
  'Sunday Afternoon/Dimanche Jour',
  'Sunday Night/Dimanche Soir',
  'Women Sunday Morning/Dimanche Matin Femme',
  'Individual Sat Night/Individuelle Samedi Soir',
  'Individual Sun Night/Individuelle Dimanche Soir',
  'Thursday/Jeudi',
]

export const DIVISION_GROUP_OPTIONS = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'ASHL Hockey 101',
]
export const DIVISION_AGE_OPTIONS = ['Open', '30+', '40+', '50+', '60+']
export const DIVISION_GENDER_OPTIONS = ['Men', 'Women', 'Coed']

const toOptions = (values: string[]) =>
  values.map((value) => ({ label: value, value }))

export interface DivisionFormState {
  name: string
  /** 'Season' | 'Conference' | 'Division'. */
  type: string
  lockRosterDate: dayjs.Dayjs | null
  sortOrder: string
  isVisibleStandings: boolean
  /**
   * Salesforce ID. Sent as `import_2_id`, read back as `salesforce_id` — the
   * legacy asymmetry, preserved.
   */
  salesforceId: string
  // Canlan registration/Salesforce block. Only rendered (and only sent) for a
  // Division on an org with `sync_salesforce`.
  divisionGroup: string
  divisionAge: string
  divisionGender: string
  dayOfWeek: string
  deposit: string
  teamCost: string
  isActive: boolean
  onlineRegistrationOpen: boolean
}

interface DivisionFormProps {
  values: DivisionFormState
  onChange: (next: DivisionFormState) => void
  fieldErrors: Record<string, string>
  /**
   * Parent's type. A child can never be created at or above its parent's level,
   * so that option is removed — mirroring the legacy form's own rule.
   */
  parentType?: string | null
  /** Root competition: its name is owned by the competition header. */
  lockName?: boolean
  /** Root, or siblings exist at this depth — changing type would be invalid. */
  lockType?: boolean
  disabled?: boolean
  /**
   * The org's `sync_salesforce`. Gates the registration/Salesforce block, which
   * legacy shows only for a Division on such an org (`showDivisionFields`).
   */
  showSalesforceSettings?: boolean
  /**
   * Canlan tenant. Gates the Salesforce ID field and the online-registration
   * toggle, which legacy gates on `isCanlan` SEPARATELY from the block above —
   * the two conditions are not the same and must not be merged.
   */
  isCanlanTenant?: boolean
}

/**
 * Create-a-division fields, built from the shared ts-components used by the
 * family and roster-player modals — not the legacy JSON `Form` config, whose
 * inputs no longer match the rest of the app.
 */
const DivisionForm = ({
  values,
  onChange,
  fieldErrors,
  parentType,
  lockName = false,
  lockType = false,
  disabled = false,
  showSalesforceSettings = false,
  isCanlanTenant = false,
}: DivisionFormProps) => {
  const set = <K extends keyof DivisionFormState>(
    key: K,
    value: DivisionFormState[K]
  ) => onChange({ ...values, [key]: value })

  const typeOptions = COMPETITION_TYPES.filter(
    (type) => !parentType || type.toLowerCase() !== parentType.toLowerCase()
  ).map((type) => ({ label: type, value: type }))

  // Shared with the modal's submit path so the fields shown and the fields sent
  // can never disagree — see `resolveDivisionFieldGates` for the legacy rules.
  const { showDivisionFields, showSalesforceId, showOnlineRegistration } =
    resolveDivisionFieldGates({
      type: values.type,
      showSalesforceSettings,
      isCanlanTenant,
    })

  return (
    <div css={formStyle}>
      <div css={rowStyle}>
        <div css={fieldStyle}>
          <TextInput
            name='name'
            value={values.name}
            setValue={(v: string) => set('name', v)}
            label={capitalizeWords('Name')}
            // Sentence case, not capitalizeWords — this is example prose, and
            // Title Case would render "E.g. ... Etc."
            placeholder='E.g. Western Conference, Monday C1'
            fieldErrors={fieldErrors}
            customError={null}
            disabled={disabled || lockName}
            required
          />
        </div>
        <div css={fieldStyle}>
          <SelectInput
            name='type'
            value={values.type}
            onChange={(v: string) => set('type', v)}
            label={capitalizeWords('Type')}
            options={typeOptions}
            fieldErrors={fieldErrors}
            customError={null}
            disabled={disabled || lockType}
          />
        </div>
        {/* Legacy puts Day of Week beside Type on the same line. */}
        {showDivisionFields && (
          <div css={fieldStyle}>
            <SelectInput
              name='dayOfWeek'
              value={values.dayOfWeek}
              onChange={(v: string) => set('dayOfWeek', v)}
              label={capitalizeWords('Day of Week')}
              options={toOptions(DAY_OF_WEEK_OPTIONS)}
              fieldErrors={fieldErrors}
              customError={null}
              disabled={disabled}
              showSearch
            />
          </div>
        )}
      </div>

      {showDivisionFields && (
        <>
          <div css={rowStyle}>
            <div css={fieldStyle}>
              <SelectInput
                name='divisionGroup'
                value={values.divisionGroup}
                onChange={(v: string) => set('divisionGroup', v)}
                label={capitalizeWords('Division Group')}
                options={toOptions(DIVISION_GROUP_OPTIONS)}
                fieldErrors={fieldErrors}
                customError={null}
                disabled={disabled}
                allowClear
              />
            </div>
            <div css={fieldStyle}>
              <SelectInput
                name='divisionAge'
                value={values.divisionAge}
                onChange={(v: string) => set('divisionAge', v)}
                label={capitalizeWords('Division Age')}
                options={toOptions(DIVISION_AGE_OPTIONS)}
                fieldErrors={fieldErrors}
                customError={null}
                disabled={disabled}
              />
            </div>
          </div>

          <div css={rowStyle}>
            <div css={fieldStyle}>
              <SelectInput
                name='divisionGender'
                value={values.divisionGender}
                onChange={(v: string) => set('divisionGender', v)}
                label={capitalizeWords('Division Gender')}
                options={toOptions(DIVISION_GENDER_OPTIONS)}
                fieldErrors={fieldErrors}
                customError={null}
                disabled={disabled}
              />
            </div>
            <div css={fieldStyle}>
              <NumberInput
                name='deposit'
                value={values.deposit}
                setValue={(v: string) => set('deposit', v)}
                // Not capitalizeWords — it would mangle the "($)" suffix.
                label='Deposit ($)'
                placeholder='0.00'
                fieldErrors={fieldErrors}
                customError={null}
                disabled={disabled}
              />
            </div>
          </div>

          <div css={rowStyle}>
            <div css={fieldStyle}>
              <NumberInput
                name='teamCost'
                value={values.teamCost}
                setValue={(v: string) => set('teamCost', v)}
                label='Team Cost ($)'
                placeholder='0.00'
                fieldErrors={fieldErrors}
                customError={null}
                disabled={disabled}
              />
            </div>
          </div>

          {/* Legacy renders this as a Yes/No Toggler. SwitchInput is the
              equivalent control in this modal's component set and carries the
              same boolean — see the note on the standings switch below. */}
          <div css={switchRowStyle}>
            <SwitchInput
              title={capitalizeWords('Is Active?')}
              subtitle='Whether this division is currently active.'
              value={values.isActive}
              onChange={(v: boolean) => set('isActive', v)}
              disabled={disabled}
            />
          </div>
        </>
      )}

      {showSalesforceId && (
        <div css={rowStyle}>
          <div css={fieldStyle}>
            <TextInput
              name='salesforceId'
              value={values.salesforceId}
              setValue={(v: string) => set('salesforceId', v)}
              label='Salesforce ID'
              fieldErrors={fieldErrors}
              customError={null}
              disabled={disabled}
            />
          </div>
        </div>
      )}

      <div css={rowStyle}>
        <div css={fieldStyle}>
          <DatePicker
            label={capitalizeWords('Roster Lock Date')}
            placeholder={capitalizeWords('Select Date')}
            value={values.lockRosterDate}
            onChange={(date) => set('lockRosterDate', date)}
            disabled={disabled}
          />
        </div>
        <div css={fieldStyle}>
          <NumberInput
            name='sortOrder'
            value={values.sortOrder}
            setValue={(v: string) => set('sortOrder', v)}
            label={capitalizeWords('Sort Order')}
            // Explicit: NumberInput's default lowercases the label
            // ("Enter sort order").
            placeholder={capitalizeWords('Enter Sort Order')}
            fieldErrors={fieldErrors}
            customError={null}
            disabled={disabled}
          />
        </div>
      </div>

      {/* Legacy's own placement: after the roster lock date, before sort order. */}
      {showOnlineRegistration && (
        <div css={switchRowStyle}>
          <SwitchInput
            title={capitalizeWords('Online Registration Open?')}
            subtitle='Allow teams to register online for this division.'
            value={values.onlineRegistrationOpen}
            onChange={(v: boolean) => set('onlineRegistrationOpen', v)}
            disabled={disabled}
          />
        </div>
      )}

      {/* SwitchInput renders the text block and the switch as SIBLINGS with no
          wrapper of its own, so this row is what puts the toggle opposite the
          label rather than stacked beneath it. */}
      <div css={switchRowStyle}>
        <SwitchInput
          title={capitalizeWords('Show Division in Standings')}
          subtitle='Show or hide this division in the standings page.'
          value={values.isVisibleStandings}
          onChange={(v: boolean) => set('isVisibleStandings', v)}
          disabled={disabled}
        />
      </div>
    </div>
  )
}

/**
 * No horizontal padding: `BaseModal` already applies `PADDING_BODY` (16px) to
 * the body, matching `PADDING_HEADER` on the title. Adding more here would
 * indent the fields relative to the modal title instead of aligning with it.
 */
const formStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${colors.NEUTRAL_0};
`

/** Two fields side by side, each taking half the row. */
const rowStyle = css`
  display: flex;
  gap: 16px;

  /* Stack on narrow viewports rather than crushing both fields. */
  @media (max-width: 560px) {
    flex-direction: column;
  }
`

/**
 * Normalises label→input spacing across the inputs.
 *
 * They disagree by design: `SelectInput` and `DatePicker` wrap their own column
 * with `gap: 8px`, while `TextInput` (and `NumberInput`, which delegates to it)
 * emit the label and control as bare siblings with no wrapper. Left alone the
 * rows visibly fail to line up. Making every field the same 8px column here
 * fixes it without touching components other screens depend on.
 */
const fieldStyle = css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  /* DatePicker wraps its whole field in a flex:1 0 0 column, which would make
     it taller than its neighbour in the same row. Neutralise that so both
     columns size from their content and the two rows line up. */
  > div {
    flex: none;
    width: 100%;
  }
`

/** Label block left, toggle right — see the note at the call site. */
const switchRowStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
`

export default DivisionForm
